import { FC, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { getPage } from '@bepro-travel/fe.shared';
import { getGuidesList } from '../service';
import { Page, PageItem } from '../types';
import { useGetPage } from '../hooks/useGetPage';
import ContentCms from './ContentCms';
import GuideAvatar from './GuideAvatar';
import {
  getGuideAvatarUrl,
  getGuideName,
  getGuidePath,
  isSameGuideName,
  matchesGuideSearch,
} from './guidesHelpers';

const GuidesPage: FC = () => {
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [searchParams] = useSearchParams();
  const highlightName = searchParams.get('guide') || '';
  const [guides, setGuides] = useState<Page[]>([]);
  const [pageContents, setPageContents] = useState<PageItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState(highlightName);
  const highlightedRef = useRef<HTMLElement | null>(null);
  const setHighlightedRef = (node: HTMLElement | null) => {
    highlightedRef.current = node;
  };

  useGetPage('');

  useEffect(() => {
    document.title = translate('guides.title');

    (async () => {
      setIsLoading(true);

      const [pages, page] = await Promise.all([getGuidesList(), getPage('guides')]);

      const contents =
        page?.pageItems?.filter(
          (item) => item.cmsAttribute?.includes('cmsContent') && item.pageContent,
        ) || [];
      setPageContents(contents);

      const sorted = (pages || [])
        .filter((guidePage) => guidePage.active !== false && guidePage.delRow !== 1)
        .sort((a, b) =>
          getGuideName(a).localeCompare(getGuideName(b), 'ru', { sensitivity: 'base' }),
        );
      setGuides(sorted);
      setIsLoading(false);
    })();
  }, []);

  const filteredGuides = useMemo(
    () => guides.filter((guide) => matchesGuideSearch(getGuideName(guide), search)),
    [guides, search],
  );

  useEffect(() => {
    if (isLoading || !highlightName || !highlightedRef.current) return;
    highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isLoading, highlightName, filteredGuides]);

  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
  };

  return (
    <div className="guides-page">
      <h1 className="guides-page-title">{translate('guides.title')}</h1>

      {!!pageContents.length && (
        <div className="guides-page-content">
          <ContentCms contents={pageContents} />
        </div>
      )}

      <form className="guides-page-search" onSubmit={onSearchSubmit}>
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={translate('guides.search_placeholder')}
          aria-label={translate('guides.search_placeholder')}
        />
        <button type="submit" aria-label={translate('guides.search')}>
          <FontAwesomeIcon icon={faSearch as any} />
        </button>
      </form>

      {isLoading && <div className="guides-page-loading">{translate('guides.loading')}</div>}

      {!isLoading && !filteredGuides.length && (
        <div className="guides-page-empty">
          {guides.length ? translate('guides.search_empty') : translate('guides.empty')}
        </div>
      )}

      {!isLoading && !!filteredGuides.length && (
        <div className="guides-page-grid">
          {filteredGuides.map((guide) => {
            const name = getGuideName(guide);
            const image = getGuideAvatarUrl(guide);
            const path = getGuidePath(guide);
            const isHighlighted = isSameGuideName(name, highlightName);
            const className = `guides-page-item${isHighlighted ? ' is-highlighted' : ''}`;
            const content = (
              <>
                <GuideAvatar className="guides-page-avatar" src={image} alt={name} />
                <div className="guides-page-name">{name}</div>
              </>
            );

            if (!path) {
              return (
                <div
                  className={className}
                  key={guide.id}
                  ref={isHighlighted ? setHighlightedRef : undefined}
                >
                  {content}
                </div>
              );
            }

            if (/^https?:\/\//i.test(path)) {
              return (
                <a
                  className={className}
                  href={path}
                  key={guide.id}
                  ref={isHighlighted ? setHighlightedRef : undefined}
                >
                  {content}
                </a>
              );
            }

            return (
              <Link
                className={className}
                to={path}
                key={guide.id}
                ref={isHighlighted ? setHighlightedRef : undefined}
              >
                {content}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GuidesPage;
